import { useMemo } from 'react';

import {
  getPlatformDisplayName,
  hasRefreshTokenPointer,
  hasVaultPointer,
  isReconnectError,
} from '@/components/settings/integration/utils';
import { usePlatformIntegrations } from '@/hooks/usePlatformIntegrations';
import { scopeGapReason } from '@/lib/platforms/scope-gap';
import type { PlatformType } from '@/services/socialAuth/types';

export interface ReconnectItem {
  platform: PlatformType;
  label: string;
  /** Calm, human reason — safe to show in a banner. */
  reason: string;
  /**
   * platform_user_id hint so OAuth re-finalizes the SAME account and skips the
   * page picker (Mora is single-account-per-platform). Falls back to identifier.
   */
  accountId: string | undefined;
}

/**
 * Single source of truth for "which connected platforms need reconnecting".
 *
 * Extracted from IntegrationSettings so every surface — the settings banner, the
 * analytics sync panel, engagement, the create flow — agrees on the SAME
 * detection. Previously the analytics panel reimplemented this and only checked
 * vault + token-expiry, ignoring the backend health-probe `account.error` and the
 * TikTok publish-scope gap, so the two pages could disagree on the count
 * (UAT 2026-06-15). The detection here mirrors the canonical settings logic.
 */
export function usePlatformNeedsReconnect(): { items: ReconnectItem[]; hasAny: boolean } {
  const { connectedAccounts } = usePlatformIntegrations();

  const items = useMemo<ReconnectItem[]>(() => {
    const out: ReconnectItem[] = [];
    const seen = new Set<string>();

    for (const account of connectedAccounts) {
      if (seen.has(account.platform)) continue;

      const accountId =
        (account.rawData as { platform_user_id?: string } | null)?.platform_user_id ??
        account.identifier;
      const push = (reason: string): void => {
        out.push({
          platform: account.platform as PlatformType,
          label: getPlatformDisplayName(account.platform as PlatformType),
          reason,
          accountId,
        });
        seen.add(account.platform);
      };

      // Backend health probe flagged the stored token unusable (orphaned / revoked
      // / expired) — the amber one-click-reconnect case, never the hard error.
      if (isReconnectError(account.error)) {
        push('Lost connection — reconnect to resume syncing.');
        continue;
      }

      const scopeGap = scopeGapReason(account);
      if (scopeGap) {
        push(scopeGap);
        continue;
      }

      // Shopify stores its token differently and never trips the vault check.
      if (!hasVaultPointer(account.rawData) && account.platform !== 'shopify') {
        push('Connection is missing credentials.');
        continue;
      }

      // An expired ACCESS token is only a problem when there is no refresh
      // token to mint a new one with. Google access tokens live one hour, so
      // testing expiry alone told every healthy Google connection to reconnect
      // for ~59 minutes out of every 60 — verified in prod on 2026-07-25, where
      // google-analytics carried a refresh pointer, an access token expired at
      // 04:30, and a successful sync at 03:30 the same morning.
      //
      // A refresh token that has actually been REVOKED surfaces through the
      // sync path (the refresh call fails), not through this clock comparison,
      // so nothing real is lost by ignoring expiry when a refresh pointer
      // exists. The same guard already exists elsewhere; this call site was
      // missed.
      const hasRefreshToken = hasRefreshTokenPointer(account.rawData);
      const expiresAt = account.tokenExpiresAt ? new Date(account.tokenExpiresAt) : null;
      if (!hasRefreshToken && expiresAt && expiresAt.getTime() < Date.now()) {
        push('Token expired — reconnect to resume syncing.');
      }
    }

    return out;
  }, [connectedAccounts]);

  return { items, hasAny: items.length > 0 };
}
